import { useAppSelector } from "../../redux/configureStore";
import { skipToken } from "@reduxjs/toolkit/dist/query/react";
import { useParams } from "react-router-dom";
import api from "./api";
import { LoadingParams } from "../../app/types";
import { isAuthedSelector } from "../auth/selectors";
import useVenueId from "../common/useVenueId";
import useSessionId from "../sessions/useSessionId";

export const useSession = (id: number | null = null) => {
  const params = useParams<LoadingParams>();
  const sessionId = params.sessionId ? parseInt(params.sessionId, 10) : -1;
  return useSessions().find(
    (session) => session.sessionId === (id ?? sessionId),
  );
};

export const useGetTenantQuery = () => api.useGetTenantQuery(true);
export const useGetUserQuery = () => api.useGetUserQuery(true);
export const useGetVenueQuery = () => {
  const venueId = useVenueId();
  const isAuthed = useAppSelector(isAuthedSelector);
  return api.useGetVenueQuery(isAuthed ? venueId : skipToken);
};

export const useRegistration = () => {
  const venueId = useVenueId();
  return api.useGetRegistrationQuery(venueId);
};

export const useGetSessionsQuery = () => {
  const venueId = useVenueId();
  const isAuthed = useAppSelector(isAuthedSelector);
  return api.useGetSessionsQuery(isAuthed ? venueId : skipToken);
};

export const useGetBoothsQuery = () => {
  const venueId = useVenueId();
  const isAuthed = useAppSelector(isAuthedSelector);
  return api.useGetBoothsQuery(isAuthed ? venueId : skipToken);
};

export const useJoinSessionQuery = () => {
  const sessionId = useSessionId();
  const isAuthed = useAppSelector(isAuthedSelector);
  return api.useJoinSessionQuery(isAuthed && sessionId ? sessionId : skipToken);
};

export const useGetConversationQuery = () => {
  const session = useSession();
  const venue = useVenue();
  return api.useGetConversationQuery(
    session?.chatTopic ?? venue?.chatTopic ?? skipToken,
  );
};

/* Wrappers around state */
export const useTenant = () => useGetTenantQuery().data;
export const useUser = () => useGetUserQuery().data;
export const useVenue = () => useGetVenueQuery().data;
export const useSessions = () => useGetSessionsQuery().data ?? [];
export const useBooths = () => useGetBoothsQuery().data ?? [];
export const useJoinData = () => useJoinSessionQuery().data;
